<!-- ============================================================
  PrivateBookingEditDialog.vue — add/edit modal for a single Private
  lesson booking. Shared between Dashboard.vue (quick "เพิ่มการจอง" ->
  "จองคลาส Private" flow) and PrivateBookingManagement.vue (the
  dedicated management grid) so both stay in sync with one
  implementation instead of two copies drifting apart.

  Usage:
    <private-booking-edit-dialog v-model="showDialog" :editing-booking="bookingOrNull"
      @saved="onSaved" @onErrorHandler="..." @onInfoHandler="..." @onLoading="..." @onClickChangeState="..." />

  Props:
    modelValue      Boolean — dialog open state (v-model)
    editingBooking  Object|null — null = add new; an object (a row from
                    /getPrivateBookingList) = edit that booking
  Emits:
    update:modelValue, saved (after a successful save — host should refetch its list),
    onErrorHandler, onInfoHandler, onLoading, onClickChangeState
============================================================ -->
<template>
  <div>
    <!-- ⚠️ ผูกกับ (open && ready) ไม่ใช่ open เฉย ๆ (เจ้าของสั่ง 2026-08-21): พ่อแม่สั่งเปิดปุ๊บ
         dialog เคยโผล่ทันทีแล้ว initForm() ค่อยไล่โหลด lookup 3 ชุด — ช่องโค้ช/นักเรียน/ราคา
         เลยทยอยเติมให้เห็นเป็นการกระพริบ · ตอนนี้ overlay โหลดคลุมไว้จนข้อมูลครบค่อยเปิด -->
    <id-modal :model-value="open && ready" @update:model-value="onModalToggle" size="lg" persistent
      :icon="isEdit ? 'mdi-pencil' : 'mdi-account-heart-outline'"
      :title="isEdit ? $t('privateBooking.editTitle') : $t('privateBooking.addNew')">
      <v-form ref="privateBookingForm">
        <!-- เตือนก่อนกดบันทึกเงิน — เตือนอย่างเดียว ปุ่มบันทึกยังกดได้ตามเดิม (ดู utils/presence.js) -->
        <div v-if="lockedBy" class="pres-warn">
          <span class="mdi mdi-alert-outline"></span>
          {{ $t('presence.alsoEditing', { name: lockedBy }) }}
        </div>
        <div class="form-grid-3">
          <div class="field"><label>{{ $t('privateBooking.coach') }} <span class="req">*</span></label>
            <id-select v-model="item.coachusername" searchable :disabled="scheduleLockedByCheckin" :placeholder="$t('common.selectCoach')"
              :options="coachLookup.map(c => ({ value: c.username, label: (c.firstname || '') + ' ' + (c.lastname || '') }))"></id-select></div>
          <div class="field"><label>{{ $t('privateBooking.type') }} <span class="req">*</span></label>
            <id-select v-model="item.privatetype" :disabled="scheduleLockedByCheckin" :placeholder="$t('common.selectType')"
              :options="[
                { value: '1on1', label: $t('customerCourse.private1on1') },
                { value: '2on1', label: $t('customerCourse.private2on1') },
                { value: '3on1', label: $t('customerCourse.private3on1') },
              ]" @update:model-value="onPrivateTypeChange"></id-select></div>
          <div class="field"><label>{{ $t('table.date') }} <span class="req">*</span></label>
            <id-date v-model="item.bookingdate" :disabled="scheduleLockedByCheckin" :placeholder="$t('common.selectDate')"></id-date></div>
        </div>
        <div v-if="scheduleLockedByCheckin" class="t-cap" style="margin-top:6px">
          <span class="mdi mdi-lock-outline"></span> {{ $t('privateBooking.scheduleLockedCheckin') }}
        </div>

        <div class="form-grid-3" style="margin-top:14px">
          <div class="field"><label>{{ $t('privateBooking.startTime') }} <span class="req">*</span></label>
            <id-select v-model="item.starttime" :disabled="scheduleLockedByCheckin" :placeholder="$t('common.selectStartTime')"
              :options="timeOptions" @update:model-value="onStartTimeChange"></id-select></div>
          <div class="field"><label>{{ $t('privateBooking.endTime') }} <span class="req">*</span></label>
            <id-select v-model="item.endtime" :disabled="scheduleLockedByCheckin" :placeholder="$t('common.selectEndTime')"
              :options="endTimeOptions" @update:model-value="maybeAutofillPrivatePrice"></id-select>
            <!-- a booking saved before start/end were split into their own fields (old free-text
                 classtime) may not land on a recognised time — surface that instead of silently
                 blocking Save forever -->
            <span v-if="(item.starttime || item.endtime) && editedPrivateBookingHours === null"
              style="font-size:12px;color:var(--c-error)">{{ $t('privateBooking.invalidClasstime') }}</span></div>
          <!-- จำนวนชั่วโมงคำนวณจากเวลาเริ่ม-สิ้นสุด จึงอยู่ต่อจากสองช่องนั้น ไม่ใช่ไปปนกับแถวเงิน
               (ย้ายมาจากแถวการชำระเงิน เพื่อให้ส่วนการชำระเงินเหลือ 4 ช่องเท่าหน้าอื่น) -->
          <div class="field"><label>{{ $t('privateBooking.hours') }}</label>
            <input class="id-input" disabled :value="privateBookingHoursLabel"></div>
        </div>

        <div class="form-grid" style="margin-top:14px">
          <div class="field" v-if="requiredStudentCount >= 1"><label>{{ $t('privateBooking.student1') }} <span class="req">*</span></label>
            <id-select v-model="item.studentid1" searchable :placeholder="$t('common.selectStudent')"
              :options="student1Options" :disabled="scheduleLockedByCheckin"></id-select></div>
          <div class="field" v-if="requiredStudentCount >= 2"><label>{{ $t('privateBooking.student2') }} <span class="req">*</span></label>
            <id-select v-model="item.studentid2" searchable :placeholder="$t('common.selectStudent')"
              :options="student2Options" :disabled="scheduleLockedByCheckin"></id-select></div>
          <div class="field" v-if="requiredStudentCount >= 3"><label>{{ $t('privateBooking.student3') }} <span class="req">*</span></label>
            <id-select v-model="item.studentid3" searchable :placeholder="$t('common.selectStudent')"
              :options="student3Options" :disabled="scheduleLockedByCheckin"></id-select></div>
        </div>

        <!-- แถวเงิน — เรียงตาม CustomerCourse.vue เป๊ะ ๆ: จ่ายแล้ว → ราคา/วิธีจ่าย → ค่าธรรมเนียม+สลิป
             ("จ่ายแล้ว" ต้องอยู่ก่อน เพราะช่องวิธีจ่ายปลดล็อกก็ต่อเมื่อติ๊กแล้ว)
             หัวข้อ "ชำระเงิน" เพิ่ม 17 ส.ค. ตามที่เจ้าของสั่ง — CustomerCourse มีอยู่แล้ว จอนี้ขาด -->
        <div class="modal-sec mt"><span class="mdi mdi-cash-multiple"></span> {{ $t('table.payment') }}</div>
        <div class="form-grid-3" style="margin-top:14px;align-items:center">
          <label class="id-cbx" style="height:42px" @click="onPaidToggle">
            <span class="id-check" :class="{ on: item.paid == 1 }"><span class="mdi mdi-check"></span></span>
            {{ item.paid ? $t('customerCourse.paid') : $t('customerCourse.unpaid') }}
          </label>
        </div>
        <!-- เรียง ราคา / วันที่ชำระ / วิธีการชำระเงิน ให้ตรงกับหน้าสร้างคอร์สและหน้าจองรายครั้งเป๊ะ ๆ
             วันที่ชำระกับวิธีจ่ายปลดล็อกเมื่อติ๊ก "จ่ายแล้ว" เท่านั้น (ยังไม่จ่าย = ยังไม่มีข้อมูลสองอย่างนี้) -->
        <div class="form-grid-3" style="margin-top:14px;align-items:end">
          <div class="field"><label>{{ $t('privateBooking.price') }} <span class="req">*</span></label>
            <id-money v-model="item.price" placeholder="0.00" @update:model-value="onPriceChanged"></id-money></div>
          <div class="field"><label>{{ $t('customerCourse.payDate') }} <span v-if="item.paid == 1" class="req">*</span></label>
            <id-date v-model="item.paydate" :placeholder="$t('common.selectDate')" :disabled="item.paid != 1"></id-date></div>
          <div class="field"><label>{{ $t('customerCourse.paymentMethod') }} <span v-if="item.paid == 1" class="req">*</span></label>
            <id-select v-model="item.paymentmethod" :placeholder="$t('customerCourse.selectPayment')"
              :options="paymentOptions" :disabled="item.paid != 1"
              @update:model-value="onPaymentMethodChanged"></id-select></div>
        </div>
        <!-- ค่าธรรมเนียมการชำระเงิน — กลไกเดียวกับหน้าคอร์สของลูกค้า: อ่านอย่างเดียวโดยปริยาย
             ระบบคำนวณจาก ราคา x อัตราค่าธรรมเนียมบัตรเครดิต เมื่อเลือก "บัตรเครดิต" วิธีอื่นเป็น 0.00
             แอดมินกดดินสอเพื่อขอแก้เองได้หลังยืนยันใน dialog -->
        <!-- เจ้าของสั่งปรับ layout 17 ส.ค.: สลิปอยู่ข้างค่าธรรมเนียม + t-cap เป็น caption ใต้ช่อง
             ค่าธรรมเนียมใน .field เดียวกัน — โครงเดียวกับ CustomerCourse.vue ต้องแก้คู่กันเสมอ -->
        <div class="form-grid-3" style="margin-top:14px;align-items:start">
          <div class="field">
            <label>{{ $t('customerCourse.paymentFee') }} <span v-if="item.paid == 1" class="req">*</span></label>
            <div class="fee-wrap">
              <id-money v-model="item.payment_fee" placeholder="0.00" :readonly="!feeManualOverride"
                :class="{ 'fee-readonly': !feeManualOverride }"></id-money>
              <button v-if="!feeManualOverride" type="button" class="fee-btn" v-hint="$t('customerCourse.paymentFeeEditBtn')"
                :aria-label="$t('customerCourse.paymentFeeEditBtn')" @click="showFeeEditConfirm = true">
                <span class="mdi mdi-pencil-outline"></span>
              </button>
              <button v-else type="button" class="fee-btn" v-hint="$t('customerCourse.paymentFeeResetBtn')"
                :aria-label="$t('customerCourse.paymentFeeResetBtn')" @click="resetFeeToAuto">
                <span class="mdi mdi-restore"></span>
              </button>
            </div>
            <div class="t-cap" style="line-height:1.5;margin-top:4px">
              <span v-if="feeManualOverride" style="color:var(--c-warning)">
                <span class="mdi mdi-pencil"></span>
                {{ $t('customerCourse.paymentFeeManualHint', { amount: formatMoney(computedPaymentFee) }) }}
              </span>
              <span v-else-if="item.paymentmethod === 'credit'">
                <span class="mdi mdi-information-outline"></span>
                {{ $t('customerCourse.paymentFeeAutoHint', { pct: feeRatePercentLabel }) }}
              </span>
              <span v-else>
                <span class="mdi mdi-information-outline"></span>
                {{ $t('customerCourse.paymentFeeNoneHint') }}
              </span>
            </div>
          </div>
          <!-- multi-slip: เลือกไฟล์ = เข้าคิว อัพโหลดจริงหลังกด "บันทึก" เท่านั้น (เจ้าของเคาะ
               2026-08-21) — จอนี้อัปคิวผ่าน /uploadPrivateBookingSlip หลังบันทึกหลักสำเร็จ (flow
               2 สเต็ปเดิม) · ใบเดิมโชว์สลิปที่บันทึกแล้วจาก /slipImages ด้วย (ลบมีผลทันทีหลัง confirm) -->
          <div class="field" style="grid-column:span 2">
            <slip-list-panel :context="item.privatebookingid ? 'private' : null" :refer="item.privatebookingid"
              :preloaded="slipPreload"
              :pending-files="pendingSlips" @update:pending-files="onPendingSlips"
              :pending-deletes="pendingSlipDeletes" @update:pending-deletes="v => pendingSlipDeletes = v"
              :payment-method="item.paymentmethod" :price-override="item.price"
              @error="msg => $emit('onErrorHandler', msg)"></slip-list-panel>
          </div>
        </div>
        <div class="field full" style="margin-top:14px"><label>{{ $t('privateBooking.note') }}</label>
          <textarea class="id-input id-textarea" v-model="item.note" maxlength="500"></textarea>
          <div class="t-cap" style="text-align:right;margin-top:4px">{{ (item.note || '').length }}/500</div></div>
      </v-form>
      <!-- audit info — only when editing an existing booking (same data as the 👁️ dialog) -->
      <template v-if="isEdit">
        <div class="modal-sec mt"><span class="mdi mdi-information-outline"></span> {{ $t('bookingMgmt.bookingInfoTitle') }}</div>
        <div class="info-rows">
          <div class="info-row">
            <span class="info-label"><span class="mdi mdi-account-plus-outline"></span> {{ $t('bookingMgmt.createBy') }}</span>
            <span class="info-val">{{ item.createby || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label"><span class="mdi mdi-clock-plus-outline"></span> {{ $t('bookingMgmt.createDate') }}</span>
            <span class="info-val">{{ format_datetime(item.createdate) || '-' }}</span>
          </div>
        </div>
      </template>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="close">{{ $t('btn.cancel') }}</button>
        <!-- ติ๊ก "จ่ายแล้ว" ต้องกรอกครบ 4 อย่าง: ราคา / วันที่ชำระ / วิธีจ่าย / ค่าธรรมเนียม
             (กฎเดียวกับหน้าคอร์สของลูกค้าและหน้าจองรายครั้ง) — ไม่งั้นเงินเข้าก้อนนั้นไปโผล่เป็น
             "ยังไม่ระบุวิธีจ่าย" ในรายงานการเงิน ลงเดือนผิด และคิดค่าธรรมเนียมบัตรเครดิตให้ไม่ได้ -->
        <button class="id-btn id-btn-primary"
          :disabled="!item.coachusername || !item.privatetype || !item.bookingdate
            || !editedPrivateBookingHours || !hasPrice || !allRequiredStudentsFilled
            || (item.paid == 1 && (!hasPayDate || !item.paymentmethod || !hasPaymentFee))"
          @click="doSave">
          <span class="mdi mdi-content-save"></span> {{ $t('btn.save') }}</button>
      </template>
    </id-modal>

    <!-- ยืนยันก่อนปลดล็อกช่องค่าธรรมเนียม — ค่าที่ระบบคำนวณให้ถูกต้องเกือบทุกครั้ง
         การแก้เองจึงควรเป็นการตัดสินใจที่ตั้งใจ ไม่ใช่พิมพ์ทับไปโดยไม่รู้ตัว -->
    <id-modal v-model="showFeeEditConfirm" size="sm" icon="mdi-pencil-outline" :title="$t('customerCourse.paymentFeeConfirmTitle')">
      <p style="margin:0">{{ $t('customerCourse.paymentFeeConfirmBody', { amount: formatMoney(computedPaymentFee) }) }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="showFeeEditConfirm = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="confirmFeeEdit">
          <span class="mdi mdi-pencil"></span> {{ $t('customerCourse.paymentFeeConfirmOk') }}</button>
      </template>
    </id-modal>

    <!-- audit D5: backend ตอบ requireConfirm เมื่อโค้ชมีใบลาวันที่เลือก — ให้แอดมินตัดสินใจ
         เองว่าจะจองทับวันลาหรือไม่ แทนที่จะบันทึกเงียบ ๆ แล้วโค้ชไม่มาสอน -->
    <id-modal v-model="showCoachLeaveConfirm" size="sm" icon="mdi-calendar-alert" :title="$t('common.coachLeaveConfirmTitle')" persistent>
      <p style="margin:0">{{ coachLeaveMessage }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="showCoachLeaveConfirm = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-warning)" @click="showCoachLeaveConfirm = false; doSave(true)">
          <span class="mdi mdi-check"></span> {{ $t('common.confirm') }}</button>
      </template>
    </id-modal>

  </div>
</template>

<script>
import axios from 'axios';
import { deleteQueuedSlips, fetchSlipImages } from '@/utils/slips';
import moment from 'moment';
import { formatDateTimeBKK } from '@/utils/datetime';
import { formatMoney } from '@/utils/money';

const DEFAULT_ITEM = {
  privatebookingid: null,
  coachusername: null,
  privatetype: null,
  studentid1: null,
  studentid2: null,
  studentid3: null,
  bookingdate: null,
  starttime: null,
  endtime: null,
  price: null,
  paid: 0,
  paydate: null,
  paymentmethod: '',
  payment_fee: '0.00',
  slip_image_url: '',
  note: null,
  createby: null,
  createdate: null,
};

export default {
  name: 'PrivateBookingEditDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    editingBooking: { type: Object, default: null },
    lockedBy: { type: String, default: null },   // username ของคนอื่นที่เปิดแก้ใบนี้อยู่
  },
  emits: ['update:modelValue', 'saved', 'onErrorHandler', 'onInfoHandler', 'onLoading', 'onClickChangeState'],
  data() {
    return {
      item: Object.assign({}, DEFAULT_ITEM),
      // ข้อมูลในฟอร์มพร้อมแสดงหรือยัง — id-modal ผูกกับ (open && ready) จึงไม่มีจังหวะที่ผู้ใช้
      // เห็น dialog เปล่า ๆ แล้วค่อยมีข้อมูลไหลเข้ามาทีละช่อง
      ready: false,
      pendingSlips: [],   // multi-slip: ไฟล์รออัปหลังบันทึกหลักสำเร็จ (ใบใหม่ยังไม่มี id)
      pendingSlipDeletes: [],   // slipid ที่กดลบไว้ — ลบจริงตอนกดบันทึกเท่านั้น
      slipPreload: null,        // รายการสลิปที่โหลดไว้ก่อนเปิด (ส่งให้พาเนลผ่าน :preloaded)
      coachLookup: [],
      privateStudentLookup: [],
      privatePrices: [], // ราคากลาง Private (tprivate_price) — ใช้ auto-fill ราคาเท่านั้น
      // อัตราค่าธรรมเนียมบัตรเครดิต — ค่าจริงมาจาก backend (/getPrivatePrices → creditCardFeeRate)
      // ซึ่งเป็น single source of truth · 0.03 ตรงนี้เป็นแค่ค่าสำรองกันหน้าพังตอนดึงไม่ได้
      creditCardFeeRate: 0.03,
      feeManualOverride: false,  // แอดมินยืนยันขอแก้ค่าธรรมเนียมเองแล้ว → หยุดคำนวณทับ
      showFeeEditConfirm: false, // dialog ยืนยันก่อนปลดล็อกช่องค่าธรรมเนียม
      showCoachLeaveConfirm: false, // audit D5: dialog ยืนยันจองทับวันลาโค้ช
      coachLeaveMessage: '',
    };
  },
  computed: {
    open: {
      get() { return this.modelValue; },
      set(v) { this.$emit('update:modelValue', v); },
    },
    isEdit() { return !!this.editingBooking; },
    // เช็คชื่อแล้ว → ล็อกเฉพาะ "กำหนดการ" (โค้ช/ประเภท/วัน/เวลา/นักเรียน) — เรื่องเงิน/โน้ต/สลิป
    // ยังแก้ได้ เพราะบันทึกจ่ายทีหลังเป็นงานปกติ (เจ้าของเคาะ 18 ส.ค.) · backend กันซ้ำ
    scheduleLockedByCheckin() {
      return !!this.editingBooking && parseInt(this.editingBooking.checkedin) === 1;
    },
    // "กรอกราคาแล้วหรือยัง" — เช็คว่าว่างไหม ห้ามใช้ !price เพราะราคา 0 บาท (สอนฟรี/ชดเชย)
    // เป็นค่าที่ตั้งใจกรอกได้จริง เดิมใช้ !item.price ทำให้พิมพ์ 0 แล้วปุ่มบันทึกปิดทันที
    // แต่พอคลิกออกจากช่อง (blur) ค่ากลายเป็นสตริง '0.00' ปุ่มกลับมาเปิดเอง = เดี๋ยวกดได้เดี๋ยวกดไม่ได้
    hasPrice() {
      const v = this.item.price;
      return v !== null && v !== undefined && String(v).trim() !== '';
    },
    // ติ๊ก "จ่ายแล้ว" = ต้องรู้ครบว่า จ่ายเท่าไหร่ / เมื่อไหร่ / ด้วยวิธีไหน / มีค่าธรรมเนียมเท่าไหร่
    // กฎเดียวกับหน้าสร้างคอร์สและหน้าจองรายครั้ง เพราะรายงานการเงินใช้ทั้ง 4 อย่างนี้จริง
    hasPayDate() {
      const v = this.item.paydate;
      return v !== null && v !== undefined && String(v).trim() !== '';
    },
    // ค่าธรรมเนียมปกติระบบเติมให้เอง จะว่างได้ก็ต่อเมื่อแอดมินกดปลดล็อกแล้วลบทิ้ง
    // เช็ค "ว่างหรือไม่" ไม่ใช่ความจริง/เท็จ เพราะ 0.00 คือค่าที่พบบ่อยที่สุด
    hasPaymentFee() {
      const v = this.item.payment_fee;
      return v !== null && v !== undefined && String(v).trim() !== '';
    },
    // Private booking modal — how many student pickers to show (1/2/3) for the chosen privatetype
    requiredStudentCount() {
      return { '1on1': 1, '2on1': 2, '3on1': 3 }[this.item.privatetype] || 0;
    },
    allRequiredStudentsFilled() {
      const ids = [this.item.studentid1, this.item.studentid2, this.item.studentid3].slice(0, this.requiredStudentCount);
      return ids.length === this.requiredStudentCount && ids.every((id) => id !== null && id !== undefined && id !== '');
    },
    // exclude students already picked in another slot, so the same child can't be
    // selected twice in a single 2on1/3on1 booking
    student1Options() {
      return this.privateStudentLookup.map((s) => ({ value: s.studentid, label: s.name }));
    },
    student2Options() {
      return this.privateStudentLookup
        .filter((s) => s.studentid !== this.item.studentid1)
        .map((s) => ({ value: s.studentid, label: s.name }));
    },
    student3Options() {
      return this.privateStudentLookup
        .filter((s) => s.studentid !== this.item.studentid1 && s.studentid !== this.item.studentid2)
        .map((s) => ({ value: s.studentid, label: s.name }));
    },
    // selectable start/end times for the Private booking modal — 06:00-22:00 every 30 min
    timeOptions() {
      const list = [];
      for (let m = 6 * 60; m <= 22 * 60; m += 30) {
        list.push(String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0'));
      }
      return list;
    },
    // end time can only be after the chosen start time
    endTimeOptions() {
      const start = this.item.starttime;
      return start ? this.timeOptions.filter((t) => t > start) : this.timeOptions;
    },
    // derived from the start/end time pickers — there's no separately-typed hours field
    editedPrivateBookingHours() {
      return this.parseStartEndHours(this.item.starttime, this.item.endtime);
    },
    privateBookingHoursLabel() {
      const h = this.editedPrivateBookingHours;
      return h !== null ? `${h} ${this.$t('common.hoursUnit')}` : '-';
    },
    // ตัวเลือกวิธีชำระเงิน — ชุดเดียวกับ CustomerCourse.vue (ค่า cash/transfer/credit ตรงกับ backend)
    paymentOptions() {
      return [
        { value: 'cash', label: this.$t('customerCourse.paymentCash') },
        { value: 'transfer', label: this.$t('customerCourse.paymentTransfer') },
        { value: 'credit', label: this.$t('customerCourse.paymentCredit') },
      ];
    },
    // ค่าธรรมเนียมที่ "ควรเป็น" ตามราคา+วิธีจ่ายปัจจุบัน — ต้องให้ผลตรงกับ computePaymentFee()
    // ฝั่ง server (server.js) เป๊ะ ๆ ทั้งสูตรและการปัดเศษ ไม่งั้นค่าที่แอดมินเห็นกับที่บันทึกจะไม่ตรงกัน
    computedPaymentFee() {
      if (this.item.paymentmethod !== 'credit') return 0;
      const price = Number(this.item.price);
      if (!Number.isFinite(price) || price <= 0) return 0;
      return Math.round(price * this.creditCardFeeRate * 100) / 100;
    },
    // 0.03 → "3" (ตัดศูนย์ท้ายทิ้ง เช่น 0.0325 → "3.25")
    feeRatePercentLabel() {
      return String(Number((this.creditCardFeeRate * 100).toFixed(4)));
    },
  },
  watch: {
    modelValue(v) {
      if (v) this.initForm();
      else this.ready = false;   // ปิดแล้วต้องกลับไปรอเตรียมข้อมูลรอบใหม่เสมอ
    },
  },
  methods: {
    SQLDate(date) {
      return moment(date).format('YYYY-MM-DD');
    },
    // แสดง createdate (TIMESTAMP จาก backend) เป็นเวลาไทยคงที่ ไม่ขึ้นกับ timezone ของเครื่องผู้ใช้
    format_datetime(value) {
      return formatDateTimeBKK(value, 'DD/MM/YYYY HH:mm');
    },
    async initForm() {
      this.$emit('onLoading', true);
      try {
        await this.buildForm();
      } finally {
        // ตั้ง ready ใน finally เสมอ — ถ้า lookup ล้ม ต้องยังเปิด dialog ให้ทำงานต่อได้
        // (แย่กว่านั้นคือค้างที่ overlay โหลดโดยไม่มี dialog โผล่เลย)
        this.ready = true;
        this.$emit('onLoading', false);
      }
    },
    async buildForm() {
      // independent lookups, each writing its own data field — run concurrently
      // instead of paying the sum of their round trips
      // ดึงทุกอย่างที่ dialog ต้องใช้พร้อมกัน (lookup 3 ชุด + รายการสลิปของใบที่กำลังแก้)
      // เวลาที่เสียก่อนเปิด = ตัวที่ช้าที่สุด ไม่ใช่ผลรวม — และ dialog จะโผล่พร้อมข้อมูลครบทีเดียว
      const editingId = this.editingBooking ? this.editingBooking.privatebookingid : null;
      const [, , , slipData] = await Promise.all([
        this.getCoachLookup(),
        this.getPrivateStudentLookup(),
        this.getPrivatePricesForModal(),
        fetchSlipImages(this.baseURL, this.$store.getters.getToken, 'private', editingId),
      ]);
      this.slipPreload = slipData;
      // คิวสลิปต้องว่างทุกครั้งที่เปิด dialog ไม่ว่าสาขาไหน (ผลรีวิว 2026-08-21: component นี้
      // mount ค้างถาวรทั้งใน Dashboard และหน้าจัดการ Private — เดิมล้างเฉพาะสาขาแก้ไข การกด ×
      // ที่หัว dialog ไม่ผ่าน close() ทำให้ไฟล์ของลูกค้าคนก่อนค้างคิว แล้วถูกอัปเข้าใบจองใบถัดไป
      // พร้อม force paid=1 — สลิปผิดใบแบบเงียบ ๆ)
      this.pendingSlips = [];
      this.pendingSlipDeletes = [];
      const b = this.editingBooking;
      if (b) {
        const students = b.students || [];
        // pre-existing bookings store classtime as one 'HH:MM-HH:MM' string — split it
        // back into the two separate start/end fields this form edits
        const classtimeParts = String(b.classtime || '').split('-').map((s) => s.trim());
        this.item = {
          privatebookingid: b.privatebookingid,
          coachusername: b.coachusername,
          privatetype: b.privatetype,
          studentid1: students[0] ? students[0].studentid : null,
          studentid2: students[1] ? students[1].studentid : null,
          studentid3: students[2] ? students[2].studentid : null,
          bookingdate: b.bookingdate ? new Date(b.bookingdate) : null,
          starttime: classtimeParts[0] || null,
          endtime: classtimeParts[1] || null,
          price: b.price,
          paid: b.paid || 0,
          // backend ส่งมาเป็น 'YYYY-MM-DD' อยู่แล้ว (DATE_FORMAT ใน SQL) — <id-date> รับรูปนี้ตรง ๆ
          // ห้ามแปลงเป็น new Date() ก่อน จะได้วันที่คลาดไป 1 วันตามโซนเวลาของเครื่องผู้ใช้
          paydate: b.paydate || null,
          paymentmethod: b.paymentmethod || '',
          payment_fee: '0.00', // ตั้งค่าจริงด้านล่าง หลังรู้ว่าเป็นค่าที่แก้เองไว้หรือให้ระบบคำนวณ
                  slip_image_url: b.slip_image_url || '',
          note: b.note,
          createby: b.createby,
          createdate: b.createdate,
        };
        // ค่าธรรมเนียมของแถวเดิม (กติกาเดียวกับ CustomerCourse.vue):
        //   NULL/ว่าง = แถวก่อนมีฟีเจอร์นี้ ไม่เคยเก็บไว้ → ให้ระบบคำนวณให้ใหม่
        //   มีค่าแต่ไม่ตรงกับที่คำนวณได้ = เคยแก้เองไว้ → ปลดล็อกช่องพร้อมป้ายเตือนตั้งแต่เปิด
        //   จะได้ไม่ถูกคำนวณทับตอนแอดมินไปแก้ราคา และมองออกทันทีว่าแถวนี้ไม่ใช่ค่ามาตรฐาน
        const storedFee = b.payment_fee;
        if (storedFee === null || storedFee === undefined || storedFee === '') {
          this.feeManualOverride = false;
          this.applyAutoFee();
        } else {
          this.item.payment_fee = Number(storedFee).toFixed(2);
          this.feeManualOverride = Number(storedFee) !== this.computedPaymentFee;
        }
      } else {
        this.item = Object.assign({}, DEFAULT_ITEM);
        this.feeManualOverride = false;
      }
    },
    // ปุ่ม × / ESC / คลิกฉากหลังของ IdModal ส่ง false กลับมา — ส่งต่อให้พ่อแม่ผ่าน open setter
    // (ไม่ผูก v-model ตรง ๆ แล้ว เพราะ model ของ modal คือ open && ready)
    onModalToggle(v) {
      if (!v) this.open = false;
    },
    async getCoachLookup() {
      const token = this.$store.getters.getToken;
      try {
        const response = await axios.get(this.baseURL + '/coachLookup', { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) this.coachLookup = response.data.results || [];
      } catch (error) {
        if (error.response && error.response.status == 401) {
          this.$emit('onErrorHandler', error.response.data.message);
          this.$emit('onClickChangeState', 'login');
        } else {
          this.$emit('onErrorHandler', error.message);
        }
      }
    },
    async getPrivateStudentLookup() {
      const token = this.$store.getters.getToken;
      try {
        const response = await axios.post(this.baseURL + '/studentLookup', {}, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) this.privateStudentLookup = response.data.results || [];
      } catch (error) {
        if (error.response && error.response.status == 401) {
          this.$emit('onErrorHandler', error.response.data.message);
          this.$emit('onClickChangeState', 'login');
        } else {
          this.$emit('onErrorHandler', error.message);
        }
      }
    },
    async getPrivatePricesForModal() {
      const token = this.$store.getters.getToken;
      try {
        const response = await axios.get(this.baseURL + '/getPrivatePrices', { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) {
          this.privatePrices = response.data.results || [];
          // อัตราค่าธรรมเนียมบัตรเครดิตมาจาก backend (single source of truth) เดินทางมากับ rate card
          if (Number.isFinite(Number(response.data.creditCardFeeRate))) {
            this.creditCardFeeRate = Number(response.data.creditCardFeeRate);
          }
        }
      } catch (error) {
        // ราคากลางเป็นแค่ตัวช่วย prefill — ดึงไม่ได้ก็ยังกรอกราคาเองได้ปกติ ไม่บล็อกการจอง
      }
    },
    // แนบสลิป = ยืนยันว่าจ่ายแล้ว (พฤติกรรมเดิมของจอนี้ — backend บังคับ paid = 1 ให้ด้วยอีกชั้น)
    onPendingSlips(files) {
      this.pendingSlips = files;
      if (files.length > 0) this.item.paid = 1;
    },
    // ── ค่าธรรมเนียมการชำระเงิน (มิเรอร์ CustomerCourse.vue ทั้งบล็อก) ──────────
    // จำนวนเงิน 2 ตำแหน่งเสมอ — สูตรกลางอยู่ที่ utils/money.js
    formatMoney,
    applyAutoFee() {
      // 2 ตำแหน่งเสมอ (เหตุผลเดียวกับ CustomerCourse.vue) — Number() เฉย ๆ ทำ 207.00 หายเป็น 207
      this.item.payment_fee = this.computedPaymentFee.toFixed(2);
    },
    // ปลด "จ่ายแล้ว" = ช่องวันที่ชำระ/วิธีจ่ายถูกล็อกกลับ ค่าที่ค้างอยู่จึงต้องล้างด้วย
    // ไม่งั้นบันทึกไปทั้งที่ช่องเทา ๆ อ่านไม่ออกว่ายังมีค่าอยู่ (ค่าธรรมเนียมก็ตามไปเป็น 0)
    onPaidToggle() {
      this.item.paid = this.item.paid ? 0 : 1;
      if (this.item.paid !== 1) {
        this.item.paymentmethod = '';
        this.item.paydate = null;
        // ติ๊กกลับเป็น "ยังไม่จ่าย" = ทิ้งสลิปที่ค้างคิวด้วย (กติกา 3 จอรับเงินต้องเหมือนกัน —
        // จอคอร์สและ walk-in ล้างอยู่แล้ว) ไม่งั้น doSave จะอัปสลิปหลังบันทึก แล้ว backend
        // force paid=1 กลับ — ได้ใบ "จ่ายแล้ว" ที่ไม่มีวันจ่าย/วิธีจ่าย ขัดกับที่เลือกบนจอ
        this.pendingSlips = [];
        this.pendingSlipDeletes = [];
        this.onPaymentMethodChanged();
      }
    },
    // แก้ราคา → ค่าธรรมเนียมตามไปด้วย เว้นแต่แอดมินขอแก้เอง (เคารพค่าที่เขาตั้งใจใส่)
    onPriceChanged() {
      if (!this.feeManualOverride) this.applyAutoFee();
    },
    // เปลี่ยนวิธีจ่าย = เปลี่ยนฐานของค่าธรรมเนียมทั้งก้อน → ล้าง override เสมอ
    onPaymentMethodChanged() {
      this.feeManualOverride = false;
      this.applyAutoFee();
    },
    confirmFeeEdit() {
      this.feeManualOverride = true;
      this.showFeeEditConfirm = false;
    },
    resetFeeToAuto() {
      this.feeManualOverride = false;
      this.applyAutoFee();
    },
    // privatetype changed — drop student picks beyond the new required count
    onPrivateTypeChange() {
      const count = this.requiredStudentCount;
      if (count < 3) this.item.studentid3 = null;
      if (count < 2) this.item.studentid2 = null;
      this.maybeAutofillPrivatePrice();
    },
    // start time changed — drop the end time if it's no longer after the new start
    onStartTimeChange() {
      const { starttime, endtime } = this.item;
      if (endtime && endtime <= starttime) this.item.endtime = null;
      this.maybeAutofillPrivatePrice();
    },
    // start/end 'HH:MM' strings -> decimal hours (or null if missing/not a valid range)
    parseStartEndHours(start, end) {
      if (!start || !end) return null;
      const [sh, sm] = String(start).split(':').map(Number);
      const [eh, em] = String(end).split(':').map(Number);
      if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return null;
      const diffMin = (eh * 60 + em) - (sh * 60 + sm);
      if (diffMin <= 0) return null;
      return Math.round((diffMin / 60) * 100) / 100;
    },
    // เติมราคาอัตโนมัติ = ชั่วโมง × price_per_hour (จาก tprivate_price ตามประเภทที่เลือก) ตอน "สร้างใหม่" เท่านั้น
    // (มิเรอร์ CustomerCourse.vue's maybeAutofillPrice — ตอนแก้ไข ราคาคือ snapshot ที่บันทึกไว้ ห้ามให้ราคากลางเขียนทับ) แก้เองได้เสมอ
    maybeAutofillPrivatePrice() {
      if (this.isEdit) return;
      const { privatetype } = this.item;
      const hours = this.editedPrivateBookingHours;
      if (!privatetype || !hours) return;
      const found = this.privatePrices.find((p) => p.privatetype === privatetype);
      if (found && found.price_per_hour !== null && found.price_per_hour !== undefined && found.price_per_hour !== '') {
        this.item.price = (Number(hours) * Number(found.price_per_hour)).toFixed(2);
        this.onPriceChanged(); // ราคาเปลี่ยน = ค่าธรรมเนียมต้องตามไปด้วย เหมือนตอนพิมพ์ราคาเอง
      }
    },
    async doSave(confirmCoachLeave = false) {
      this.$emit('onLoading', true);
      const isEdit = this.isEdit;
      const studentids = [this.item.studentid1, this.item.studentid2, this.item.studentid3]
        .slice(0, this.requiredStudentCount)
        .filter((id) => id !== null && id !== undefined && id !== '');
      const payload = {
        coachusername: this.item.coachusername,
        bookingdate: this.SQLDate(this.item.bookingdate),
        classtime: this.item.starttime + '-' + this.item.endtime,
        privatetype: this.item.privatetype,
        hours: this.editedPrivateBookingHours,
        price: this.item.price,
        paid: this.item.paid ? 1 : 0,
        // ส่งเสมอ (ไม่ใช่ส่งเฉพาะตอนมีค่า) — backend ใช้ "ส่งมาไหม" แยกระหว่าง client เก่า
        // ที่ไม่รู้จักฟีเจอร์นี้ กับการตั้งใจล้างวิธีจ่าย ถ้าไม่ส่งมันจะไปยึดค่าเดิมในแถวแทน
        paydate: this.item.paydate ? this.SQLDate(this.item.paydate) : '',
        paymentmethod: this.item.paymentmethod || '',
        payment_fee: this.item.payment_fee !== null && this.item.payment_fee !== undefined && this.item.payment_fee !== ''
          ? this.item.payment_fee : 0,
        slip_image_url: this.item.slip_image_url || null,
        note: this.item.note || null,
        studentids,
      };
      // audit D5: รอบสองหลังแอดมินกดยืนยันจองทับวันลาโค้ช — backend เช็ค === true เท่านั้น
      // เช็ค === true เพราะปุ่มบันทึกหลักเรียก @click="doSave" ซึ่งส่ง MouseEvent (truthy) เข้ามา
      if (confirmCoachLeave === true) payload.confirmCoachLeave = true;
      if (isEdit) payload.privatebookingid = this.item.privatebookingid;
      const token = this.$store.getters.getToken;
      const url = isEdit ? '/updatePrivateBooking' : '/addPrivateBooking';
      try {
        const response = await axios.post(this.baseURL + url, payload, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data.success) {
          const savedId = isEdit ? this.item.privatebookingid : response.data.privatebookingid;
          // ลบสลิปที่มาร์กไว้ — ทำ **หลังบันทึกสำเร็จ** และ **ก่อน** อัปใบใหม่ (ผลรีวิว 2026-08-23):
          //  • ติดยืนยันวันลาโค้ชแล้วผู้ใช้กดยกเลิก / บันทึกพัง = ไม่มีสลิปใบไหนหายไป
          //  • ลบก่อนอัป ทำให้เพดาน 3 ใบนับจากของจริงหลังลบ สลับใบจึงไม่ถูกปฏิเสธ
          //  • คอลัมน์ slip_image_url ที่คำขอบันทึกเพิ่งเขียนทับ ถูก syncLatestSlip ฝั่ง backend
          //    แก้ให้ตรงกับใบที่เหลือทันทีหลังลบ
          if (this.pendingSlipDeletes.length > 0 && savedId) {
            const delResult = await deleteQueuedSlips(this.baseURL, token, this.pendingSlipDeletes);
            this.pendingSlipDeletes = delResult.failed;   // ใบที่ลบไม่สำเร็จค้างคิวไว้ให้ลองใหม่
            if (delResult.failed.length > 0) this.$emit('onErrorHandler', this.$t('customerCourse.slipDeleteFail'));
          }
          let sv = null; // ผลตรวจสลิป (SlipOK) จาก /uploadPrivateBookingSlip — null ถ้าไม่ได้แนบสลิปใหม่
          if (this.pendingSlips.length > 0 && savedId) {
            sv = await this.uploadPrivateBookingSlip(this.pendingSlips, savedId);
          }
          const message = response.data.message || this.$t(isEdit ? 'msg.updated' : 'msg.bookingAdded');
          if (sv && ['duplicate', 'wrong_receiver', 'amount_mismatch', 'failed'].includes(sv.status)) {
            // บันทึกสำเร็จแล้ว แต่ผลตรวจสลิปมีปัญหา — เตือนแดงให้ admin กลับไปตรวจ
            this.$emit('onErrorHandler', `${this.$t('customerCourse.slipSaveWarn')}: ${sv.reason || sv.status}`);
          } else if (sv && sv.status === 'verified') {
            this.$emit('onInfoHandler', `${message} — ${this.$t('customerCourse.slipSaveVerified')}`);
          } else {
            this.$emit('onInfoHandler', message);
          }
          this.close();
          this.$emit('saved');
        } else if (response.data.requireConfirm && response.data.confirmKind === 'coachLeave') {
          this.coachLeaveMessage = response.data.message || '';
          this.showCoachLeaveConfirm = true;
        } else {
          this.$emit('onErrorHandler', response.data.message || this.$t(isEdit ? 'msg.updateFail' : 'msg.bookingAddFail'));
        }
      } catch (error) {
        if (error.response && error.response.status == 401) {
          this.$emit('onErrorHandler', error.response.data.message);
          this.$emit('onClickChangeState', 'login');
        } else {
          this.$emit('onErrorHandler', (error.response && error.response.data && error.response.data.message) || error.message);
        }
      } finally {
        this.$emit('onLoading', false);
      }
    },
    // uploaded separately from the main save (mirrors EditProfile.vue's own profile-image
    // upload) — backend /uploadPrivateBookingSlip takes 'slipImage' + 'privatebookingid',
    // stores the slip, verifies it with SlipOK, and returns { url, slipVerification }
    // คืนค่า slipVerification ให้ doSave ใช้ปรับข้อความแจ้งผล (null = อัพโหลดพัง/ไม่มีผลตรวจ)
    async uploadPrivateBookingSlip(files, privatebookingid) {
      const formData = new FormData();
      // multi-slip: ส่งได้หลายไฟล์ใน field เดิม · ไม่ต้อง rename เองแล้ว — backend ตั้งชื่อคีย์บน
      // Spaces จาก privatebookingid ให้ (uploadSlipImageToS3) และเติม _1, _2 ให้เมื่อชื่อชน
      files.forEach((f) => formData.append('slipImage', f));
      formData.append('privatebookingid', String(privatebookingid));
      const token = this.$store.getters.getToken;
      try {
        const response = await fetch(this.baseURL + '/uploadPrivateBookingSlip', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await response.json();
        if (!data || !data.success) {
          this.$emit('onErrorHandler', (data && data.message) || this.$t('msg.uploadFail'));
          return null;
        }
        return data.slipVerification || null;
      } catch (e) {
        this.$emit('onErrorHandler', this.$t('msg.uploadFail'));
        return null;
      }
    },
    close() {
      this.open = false;
      this.pendingSlips = [];        // ไฟล์ที่ค้างในคิวห้ามติดไปใบถัดไป
      this.pendingSlipDeletes = [];  // เครื่องหมายลบก็เช่นกัน — ยกเลิก dialog = ไม่ลบอะไรเลย
      this.slipPreload = null;       // ข้อมูลสลิปของใบก่อนห้ามไปโผล่ในใบถัดไป
      setTimeout(() => {
        this.item = Object.assign({}, DEFAULT_ITEM);
      }, 300);
    },
  },
};
</script>
